import React, { useEffect, useMemo } from "react";
import { useParams, useHistory, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { makeStyles, Theme } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import Button from "@material-ui/core/Button";
import Paper from "@material-ui/core/Paper";
import CircularProgress from "@material-ui/core/CircularProgress";
import { SellingAreaState, SellingAreaStatus } from "../state";
import { cartAction } from "../state/actions";
import PaymentMethods from "../components/PaymentMethods";
import PayButton from "../components/PayButton";
import AppBar from "../../../components/AppBar";

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    width: "100%",
    paddingBottom: "160px",
  },
  backButton: {
    margin: theme.spacing(0, 0, 0, 1),
  },
  title: {
    flexGrow: 1,
  },
  card: {
    margin: theme.spacing(2),
    padding: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: `${theme.shape.borderRadius}px`,
  },
  cardHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: theme.spacing(1),
  },
  cardHeaderContent: {
    minWidth: 0,
  },
  cardHeaderPrice: {
    whiteSpace: "nowrap",
    flexShrink: 0,
  },
  itemLabel: {
    color: theme.palette.text.secondary,
  },
  bottomBar: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    position: "fixed" as const,
    bottom: 0,
    left: 0,
    right: 0,
    padding: theme.spacing(1, 2, 2),
    backgroundColor: theme.palette.background.default,
    zIndex: 10,
  },
  totalRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  removeButton: {
    paddingBlockStart: `${theme.spacing(3)}px`,
    [theme.breakpoints.down("sm")]: {
      display: "flex",
      justifyContent: "center",
    },
  },
}));

export default function BundleRecapPage() {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation<{
    selectedByCategory?: Record<string, number>;
  }>();
  const params = useParams<{ id: string; bundleId: string }>();
  const dispatch = useDispatch();

  const bundle = useSelector((state: SellingAreaState) => state.bundle);
  const catalog = useSelector((state: SellingAreaState) => state.catalog);
  const cart = useSelector((state: any) => state.cart);
  const status = useSelector((state: SellingAreaState) => state.status);
  const wallet = useSelector((state: SellingAreaState) => state.wallet);
  const corporateCredit = useSelector(
    (state: SellingAreaState) => state.corporateCredit,
  );

  const selectedByCategory = location.state?.selectedByCategory || {};
  const currentBundle = bundle?.find((b) => b.id === params.bundleId);

  const bundlePrice = currentBundle?.defaultPrice ?? 0;
  const walletBalance = wallet?.balance ?? 0;
  const corporateCreditBalance = corporateCredit?.balance ?? 0;

  const selectedItems = useMemo(() => {
    if (!catalog || !currentBundle) return [];
    const items: any[] = [];
    catalog.categories.forEach((category: any) => {
      const selectedId = selectedByCategory[category.id];
      if (selectedId !== undefined) {
        const item = category.items.find((i: any) => i.id === selectedId);
        if (item) items.push(item);
      }
    });
    return items;
  }, [catalog, currentBundle, selectedByCategory]);

  useEffect(() => {
    if (cart && currentBundle && cart.totalWithTax !== bundlePrice) {
      dispatch(
        cartAction.overrideCartTotal({
          totalWithTax: bundlePrice,
          walletBalance,
          corporateCreditBalance,
        }),
      );
    }
  }, [
    cart,
    bundlePrice,
    walletBalance,
    corporateCreditBalance,
    currentBundle,
    dispatch,
  ]);

  useEffect(() => {
    if (status === SellingAreaStatus.DATA_FETCHED && !cart) {
      history.push(`/selling-area/${params.id}/bundle/${params.bundleId}${location.search}`);
    }
  }, [status, cart, params.id, params.bundleId, history, location.search]);

  useEffect(() => {
    if (new URLSearchParams(location.search).get('mode') === 'preview') {
      history.push(`/selling-area/${params.id}/bundle/${params.bundleId}${location.search}`);
      return;
    }
    if (!sessionStorage.getItem("bundleRecapAllowed")) {
      history.push(`/selling-area/${params.id}/bundle/${params.bundleId}`);
      return;
    }

    const clearToken = () => sessionStorage.removeItem("bundleRecapAllowed");
    window.addEventListener("beforeunload", clearToken);
    return () => window.removeEventListener("beforeunload", clearToken);
  }, [params.id, params.bundleId, history, location.search]);

  if (!currentBundle) return <CircularProgress />;

  const originalPrice = parseFloat(
    selectedItems
      .reduce((sum, item) => sum + (item.price?.value || 0), 0)
      .toFixed(2),
  );
  const saving = parseFloat((originalPrice - bundlePrice).toFixed(2));

  const handleRemove = () => {
    dispatch(cartAction.emptyCart());
    sessionStorage.removeItem("bundleRecapAllowed");
    history.push(`/selling-area/${params.id}/bundle/${params.bundleId}${location.search}`);
  };

  return (
    <div className={classes.root}>
      <AppBar
        position="fixed"
        enableBack
        back={handleRemove}
        title="Ci siamo quasi"
      />

      <div className={classes.card}>
        <div className={classes.cardHeader}>
          <div className={classes.cardHeaderContent}>
            <Typography variant="subtitle1">{currentBundle.title}</Typography>
            {selectedItems.map((item) => (
              <Typography
                key={item.id}
                variant="body2"
                className={classes.itemLabel}
              >
                {item.label}
              </Typography>
            ))}
          </div>
          <Typography variant="subtitle1" className={classes.cardHeaderPrice}>{bundlePrice} €</Typography>
        </div>
        <div className={classes.removeButton}>
          <Button
            color="primary"
            size="small"
            variant="outlined"
            onClick={handleRemove}
          >
            RIMUOVI
          </Button>
        </div>
      </div>

      <Paper className={classes.bottomBar} square elevation={24}>
        <div className={classes.totalRow}>
          <Typography component="h6" variant="subtitle1">
            Totale (1 Prodotto):
          </Typography>
          <Typography component="h6" variant="subtitle1">
            {bundlePrice} €
          </Typography>
        </div>
        {originalPrice > 0 && saving > 0 && (
          <div className={classes.totalRow}>
            <Typography variant="subtitle2">Risparmio menu</Typography>
            <Typography variant="subtitle2">{saving} €</Typography>
          </div>
        )}
        <PaymentMethods />
        {cart && <PayButton />}
      </Paper>
    </div>
  );
}
